import React, { useState, useCallback, useEffect } from "react";
import { DefaultSettings } from "../admin/components/GVSettingsModal";
import sortCompletions from "../utilities/sortCompletions";
import hexToRGB from "../utilities/hexToRGB";
import TractParser from "../utilities/TractParser";
import { useHotkeys } from "@mantine/hooks";
import { useEvent } from "@harelpls/use-pusher";
import { Completions as CompletionsSvc } from "../services/ActiveAuctionService";
import { useContext } from "react";
import { AdminAuctionContext } from "../admin/context/AdminAuctionContext";
import { useQuery } from "react-query";

export const GraphicalViewerContext = React.createContext();

const completionColors = ["#204077", "#b91c1c", "#6b7280"];

export default function GraphicalViewerProvider({ children }) {
    const [isDragging, setIsDragging] = useState(false);

    const { currentAuctionId, auctionChannel } =
        useContext(AdminAuctionContext);

    const PageView = Object.freeze({
        TableView: 1,
        MapView: 2,
        HybridView: 3,
    });

    const [completionIndex, setCompletionIndex] = useState(0);

    const getSavedSettings = () => {
        const savedData = localStorage.getItem("gvsettings");
        return {
            ...DefaultSettings,
            ...(savedData && JSON.parse(savedData)),
        };
    };
    const [settings, setSettings] = useState(getSavedSettings);

    const completionsQuery = useQuery(
        ["completions", currentAuctionId, settings?.completionsToShow],
        () =>
            CompletionsSvc({
                auction_id: currentAuctionId,
                per_page: settings?.completionsToShow || 3,
            }),
        {
            enabled: !!currentAuctionId,
            select: (data) => sortCompletions(data.data),
        }
    );
    const { data: completionsData = [] } = completionsQuery;

    const completion = completionsData[completionIndex];
    const [currentTotalAcres, setCurrentTotalAcres] = useState(0);

    const [currentView, setCurrentView] = useState(PageView.TableView);
    const showTableView = currentView === PageView.TableView;
    const showMapView = currentView === PageView.MapView;
    const showHybridView = currentView === PageView.HybridView;

    const [isPlaying, setIsPlaying] = useState(false);
    const [showIsPlaying, setShowIsPlaying] = useState(true);

    // const nextView = () => {
    //     // go to the next view, if HybridView, go to TableView
    //     setCurrentView(
    //         currentView === PageView.HybridView
    //             ? PageView.TableView
    //             : currentView + 1
    //     );
    // };

    // const previousView = () => {
    //     // go to the previous view, if TableView, go to HybridView
    //     setCurrentView(
    //         currentView === PageView.TableView
    //             ? PageView.HybridView
    //             : currentView - 1
    //     );
    // };

    const nextCompletion = useCallback(() => {
        // if we're at the last completion, circle to the first one
        const newIndex =
            completionIndex === completionsData.length - 1
                ? 0
                : completionIndex + 1;
        setCompletionIndex(newIndex);

        const scrollContainer = document.querySelector("#gv-scroll-container");
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
    }, [completionIndex, completionsData, setCompletionIndex]);

    const previousCompletion = () => {
        // if we're at the first completion, circle to the last one
        const newIndex =
            completionIndex === 0
                ? completionsData.length - 1
                : completionIndex - 1;
        setCompletionIndex(newIndex);
    };

    useEffect(() => {
        if (settings) {
            localStorage.setItem("gvsettings", JSON.stringify(settings));
        }
    }, [settings]);

    useEffect(() => {
        if (settings.currentView) {
            setCurrentView(settings.currentView);
        }
    }, [settings.currentView]);

    // Pagination Support
    const paginationDuration =
        (settings.transitionDuration ? settings.transitionDuration : 10) * 1000;
    const [paginationCountdown, setPaginationCountdown] =
        useState(paginationDuration);
    const [bidPage, setBidPage] = useState(1);
    const [bidPages, setBidPages] = useState(1);
    const shouldUsePagination = settings.transitionMode === "pagination";

    const setCurrentCompletionIndex = (idx) => {
        if (idx >= 0 && idx < completionsData.length) {
            setCompletionIndex(idx);
            setBidPage(1);
        }
    };

    const nextPage = useCallback(() => {
        if (bidPage < bidPages) {
            setBidPage(bidPage + 1);
        } else {
            setBidPage(1);
        }
        setPaginationCountdown(paginationDuration);
    }, [bidPage, bidPages, paginationDuration]);

    const previousPage = () => {
        if (bidPage > 1) {
            setBidPage(bidPage - 1);
        } else {
            setBidPage(bidPages);
        }
        setPaginationCountdown(paginationDuration);
    };

    useEffect(() => {
        if (bidPages === 1) {
            setShowIsPlaying(false);
        } else {
            setShowIsPlaying(true);
        }
    }, [bidPages]);

    // Hot Keys
    useHotkeys([
        ...(shouldUsePagination
            ? [
                  ["ArrowDown", nextPage],
                  ["ArrowUp", previousPage],
              ]
            : [
                  ["ArrowDown", nextCompletion],
                  ["ArrowUp", previousCompletion],
              ]),
        ["Space", () => setIsPlaying(!isPlaying)],
        ["mod+1", () => setCurrentCompletionIndex(0)],
        ["mod+2", () => setCurrentCompletionIndex(1)],
        ["mod+3", () => setCurrentCompletionIndex(2)],
        ["mod+4", () => setCurrentCompletionIndex(3)],
        ["mod+5", () => setCurrentCompletionIndex(4)],
        ["mod+6", () => setCurrentCompletionIndex(5)],
        ["mod+7", () => setCurrentCompletionIndex(6)],
        ["mod+8", () => setCurrentCompletionIndex(7)],
        ["mod+9", () => setCurrentCompletionIndex(8)],
        ["mod+0", () => setCurrentCompletionIndex(9)],
        ["Equal", () => increaseDuration()],
        ["Minus", () => decreaseDuration()],
    ]);

    useEvent(auctionChannel, "App\\Events\\CompletionsUpdated", () => {
        completionsQuery.refetch();
    });
    const [bidderTractInfo, setBidderTractInfo] = useState([]);

    // Fixed the refreshBidderTractInfo function to avoid the infinite recursion
    const refreshBidderTractInfo = useCallback((completion) => {
        if (!completion) return;

        const newBidderTractInfo = [];
        let totalAcres = 0;

        if (completion?.bids?.length > 0) {
            completion.bids.forEach((bid) => {
                newBidderTractInfo.push({
                    bidder_id: bid.bid_bidder_id,
                    tracts: TractParser(bid.bid_name).tractArray,
                    bidder_color: hexToRGB(bid.bidder.bidder_color),
                });
                bid.tracts.forEach((tract) => {
                    totalAcres += +tract.tract_acres;
                });
            });
            setCurrentTotalAcres(totalAcres);
        }

        setBidderTractInfo(newBidderTractInfo);
    }, []);

    // Completion Logic
    useEffect(() => {
        refreshBidderTractInfo(completionsData[completionIndex]);
    }, [completionsData, completionIndex, refreshBidderTractInfo]);

    // TODO
    // Scroll Logic
    // const scrollDuration = (settings.transitionDuration ? settings.transitionDuration : 10) * 1000;
    // const scrollStep = 70;
    // const pixelsPerStep = 3;

    // const scrollContainer = document.querySelector("#gv-scroll-container");
    // // const scrollableHeight = (scrollContainer?.scrollHeight ??0 ) - scrollContainer.getBoundingClientRect()?.height ?? 0;
    // const scrollableHeight = 100;
    // const numberOfSteps = scrollableHeight / pixelsPerStep;
    // const scrollDuration = numberOfSteps * 70;
    const scrollDuration = 15 * 1000;
    const [scrollCountdown] = useState(scrollDuration);

    // console.log(scrollableHeight, scrollDuration, scrollStep, pixelsPerStep);
    // const scrollEndDelay = 3000;
    // useEffect(() => {
    //     const id = setInterval(() => {
    //         if (isPlaying && !shouldUsePagination) {
    //             setScrollCountdown((prev) => {
    //                 if (prev <= 0) {
    //                     nextCompletion();
    //                     return scrollDuration;
    //                 }
    //                 return prev - scrollStep;
    //             });

    //             // const scrollableTime = scrollDuration - scrollEndDelay;
    //             // const scrollTimeProgress = (scrollCountdown + scrollEndDelay) / scrollableTime;
    //             // const scrollOffset = 1 - scrollTimeProgress;
    //             const scrollOffset = 1 - (scrollCountdown / scrollDuration);

    //             scrollContainer.scrollTop += pixelsPerStep;
    //         }
    //     }, scrollStep);
    //     return () => clearInterval(id);
    // }, [isPlaying, nextPage, scrollDuration, nextCompletion, pixelsPerStep, scrollCountdown, shouldUsePagination]);

    // Pagination Logic
    const paginationStep = 50;
    useEffect(() => {
        const id = setInterval(() => {
            if (isPlaying && shouldUsePagination) {
                setPaginationCountdown((prev) => {
                    if (prev <= 0) {
                        nextPage();
                        return paginationDuration;
                    }
                    return prev - paginationStep;
                });
            }
        }, paginationStep);

        return () => clearInterval(id);
    }, [isPlaying, nextPage, paginationDuration, shouldUsePagination]);

    let progress = (scrollCountdown / scrollDuration) * 100;
    if (shouldUsePagination) {
        progress = (paginationCountdown / paginationDuration) * 100;
    }

    const updateTransitionDuration = (duration) => {
        if (duration < 5) {
            duration = 5;
        }
        if (duration > 100) {
            duration = 100;
        }
        setSettings((prev) => ({ ...prev, transitionDuration: duration }));
        setPaginationCountdown(() => duration * 1000);
    };

    const increaseDuration = () => {
        updateTransitionDuration(settings.transitionDuration + 1);
    };
    const decreaseDuration = () => {
        updateTransitionDuration(settings.transitionDuration - 1);
    };

    return (
        <GraphicalViewerContext.Provider
            value={{
                completion,
                completionsData,
                bidderTractInfo,
                completionIndex,
                completionColors,
                currentTotalAcres,
                // Settings
                settings,
                setSettings,
                getSavedSettings,
                // Visibility
                currentView,
                setCurrentView,
                showTableView,
                showMapView,
                showHybridView,

                // Pagination
                bidPage,
                bidPages,
                setBidPage,
                setBidPages,

                shouldUsePagination,

                isPlaying,
                setIsPlaying,
                showIsPlaying,
                progress,
                completionsQuery,

                isDragging,
                setIsDragging,
            }}
        >
            {children}
        </GraphicalViewerContext.Provider>
    );
}
